/**
 * Stockage des préférences, derrière une interface.
 *
 * Sur le web, `localStorage` suffit et répond de façon synchrone. En natif,
 * iOS peut vider le stockage d'une webview sous pression mémoire, et l'API
 * recommandée (`@capacitor/preferences`) est asynchrone.
 *
 * D'où la forme retenue : `hydrate()` charge tout en mémoire une fois au
 * démarrage, puis les lectures sont synchrones et les écritures partent sans
 * qu'on attende leur retour. Cela permet aux deux mondes de partager
 * exactement le même code appelant.
 */
export interface Preferences {
  /** À appeler une fois au démarrage. Sans effet sur le web. */
  hydrate(): Promise<void>;
  read<T>(key: string, fallback: T): T;
  write(key: string, value: unknown): void;
  remove(key: string): void;
}

export function createWebPreferences(): Preferences {
  return {
    async hydrate() {
      // localStorage est déjà disponible et synchrone.
    },

    read<T>(key: string, fallback: T): T {
      try {
        const raw = localStorage.getItem(key);
        if (raw === null) return fallback;
        const parsed = JSON.parse(raw) as T | null;
        return parsed ?? fallback;
      } catch {
        // Navigation privée stricte, quota dépassé, JSON corrompu : dans tous
        // les cas, mieux vaut la valeur par défaut qu'une page cassée.
        return fallback;
      }
    },

    write(key: string, value: unknown): void {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Écriture impossible : l'application continue sans mémoire.
      }
    },

    remove(key: string): void {
      try {
        localStorage.removeItem(key);
      } catch {
        // Sans conséquence.
      }
    },
  };
}
